import { ArrowRight, BookOpenText, HandHelping, MessageSquareText } from 'lucide-react'

import { useLanguage } from '../shared/LanguageContext'
import { displayDate } from './taFormat'
import { taCopy } from './taCopy'
import { SectionUnavailable } from './TaShell'

function dueState(task, today) {
  const due = String(task?.due_at || '').slice(0, 10)
  if (!due) return null
  if (due < today) return 'overdue'
  const soon = new Date(`${today}T12:00:00`)
  soon.setDate(soon.getDate() + 3)
  return due <= soon.toISOString().slice(0, 10) ? 'dueSoon' : null
}

function dateParts(value, language) {
  const parsed = new Date(`${value}T12:00:00`)
  const locale = language === 'cn' ? 'zh-CN' : 'en-US'
  return {
    month: new Intl.DateTimeFormat(locale, { month: 'short' }).format(parsed).toUpperCase(),
    // Digits only — zh-CN '2-digit' yields "28日" and the 日 suffix wraps
    // to its own line inside the big calendar tile.
    day: String(parsed.getDate()).padStart(2, '0'),
    weekday: new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(parsed).toUpperCase(),
  }
}

export default function TaHomeView({ home, onNavigate }) {
  const { language } = useLanguage()
  const copy = taCopy(language)
  const schedule = home.schedule?.status === 'ready' ? home.schedule.data : null
  const tasks = home.tasks?.status === 'ready' ? home.tasks.data : null
  const requests = home.requests?.status === 'ready' ? home.requests.data : null
  const next = schedule?.upcoming_sessions?.[0] || null
  const nextDate = next ? dateParts(next.session_date, language) : null
  const attention = (tasks || [])
    .filter((task) => ['open', 'reopened'].includes(task.status))
    .map((task) => ({ ...task, dueState: dueState(task, home.today) }))
    .filter((task) => task.dueState)
  const openRequests = (requests || []).filter((row) => ['submitted', 'acknowledged'].includes(row.status))

  return (
    <div className="ta-v2-view ta-v2-home">
      <section className="ta-v2-next" aria-labelledby="ta-next-heading">
        <p className="ta-v2-section-label">{copy('nextClass')}</p>
        {home.schedule?.status !== 'ready' ? <SectionUnavailable /> : next ? (
          <>
            <a
              href="#schedule"
              className="ta-v2-next-agenda"
              aria-label={`${next.class_name}, ${displayDate(next.session_date, language, { weekday: true })}`}
              onClick={(event) => {
                event.preventDefault()
                onNavigate('schedule')
              }}
            >
              <time className="ta-v2-next-calendar" dateTime={next.session_date}>
                <span>{nextDate.month}</span>
                <strong>{nextDate.day}</strong>
                <span>{nextDate.weekday}</span>
              </time>
              <span className="ta-v2-next-copy">
                <h2 id="ta-next-heading">{next.class_name}</h2>
                <span className="ta-v2-next-meta">
                  <span>{next.time_display || '—'}</span>
                  <span>{copy('session')} {next.session_number}</span>
                  {next.location && <span>{next.location}</span>}
                </span>
              </span>
              <ArrowRight aria-hidden="true" size={19} />
            </a>
            <button type="button" className="ta-v2-text-action" onClick={() => onNavigate('schedule')}>
              {copy('futureClasses')} <ArrowRight aria-hidden="true" size={17} />
            </button>
          </>
        ) : (
          <h2 id="ta-next-heading" className="ta-v2-empty-title">{copy('noUpcoming')}</h2>
        )}
      </section>

      <section className="ta-v2-section" aria-labelledby="ta-attention-heading">
        <div className="ta-v2-section-heading">
          <h2 id="ta-attention-heading">{copy('needsAttention')}</h2>
          <span>{attention.length + openRequests.length}</span>
        </div>
        {attention.length || openRequests.length ? (
          <div className="ta-v2-ruled-list">
            {attention.map((task) => (
              <button key={task.id} type="button" className="ta-v2-row-button" onClick={() => onNavigate('work')}>
                <span><strong>{task.title}</strong><small>{copy(task.dueState)} · {displayDate(task.due_at, language)}</small></span>
                <ArrowRight aria-hidden="true" size={17} />
              </button>
            ))}
            {openRequests.map((request) => (
              <button key={request.id} type="button" className="ta-v2-row-button" onClick={() => onNavigate('me')}>
                <span><strong>{copy(request.category)}</strong><small>{copy(request.status)}</small></span>
                <ArrowRight aria-hidden="true" size={17} />
              </button>
            ))}
          </div>
        ) : null}
        {(home.tasks?.status !== 'ready' || home.requests?.status !== 'ready') && <SectionUnavailable />}
        {home.tasks?.status === 'ready' && home.requests?.status === 'ready' && !attention.length && !openRequests.length && (
          <p className="ta-v2-empty">{copy('allClear')}</p>
        )}
      </section>

      <section className="ta-v2-section" aria-labelledby="ta-quick-heading">
        <div className="ta-v2-section-heading"><h2 id="ta-quick-heading">{copy('quickActions')}</h2></div>
        <div className="ta-v2-quick-grid">
          <button type="button" onClick={() => onNavigate('work', 'observation')}>
            <BookOpenText aria-hidden="true" size={20} />
            <span>{copy('addObservation')}</span>
          </button>
          <button type="button" onClick={() => onNavigate('work', 'support')}>
            <HandHelping aria-hidden="true" size={20} />
            <span>{copy('logSupport')}</span>
          </button>
          <button type="button" onClick={() => onNavigate('me', 'request')}>
            <MessageSquareText aria-hidden="true" size={20} />
            <span>{copy('contactMatt')}</span>
          </button>
        </div>
      </section>
    </div>
  )
}
